<ts-dialog title="Associate Contact" (closeDialog)="discard()">
  <main ts-dialog-body class="flex flex-col flex-1 w-full">
    <form [formGroup]="contactForm">
      <!-- Contact Search/Name & ID Control -->
      <div class="flex flex-col gt-xs:flex-row">
        <!-- Contact ID Control (hidden) -->
        <input [formControlName]="'id'" type="hidden" />
        <!-- Company Search/Name Control -->
        <mat-form-field class="flex-auto">
          <mat-label>Contact Name</mat-label>
          <input
            matInput
            [formControlName]="'name'"
            [placeholder]="'Search by Contact Name'"
            [matAutocomplete]="matAutocompleteContact"
            [spellcheck]="false"
            [readonly]="hasSelectedContact"
          />
          @if (isSearching) {
            <div [ngTemplateOutlet]="LoadingSpinner"></div>
          }
        </mat-form-field>
        <mat-autocomplete
          #matAutocompleteContact="matAutocomplete"
          [disableRipple]="true"
          (optionSelected)="autocompleteContactSelected($event)"
        >
          @if (contactResults && !contactResults.length) {
            <mat-option disabled class="no-results">No contacts found!</mat-option>
          }

          @for (contactResult of contactResults; track contactResult.id) {
            <mat-option [value]="contactResult">
              <span
                [innerHTML]="
                  resultLabel(contactResult)
                    | highlighter: contactForm.get('name').value : 'highlight-result' : 'partial'
                "
              ></span>
            </mat-option>
          }

          @if (
            canCreateContact() &&
            contactResults &&
            contactNameControl.value &&
            !idControl.value &&
            !createNew &&
            !isSearching
          ) {
            <mat-option disabled (click)="isNewContact()">
              <span class="create-new">Create a New Contact</span>
            </mat-option>
          }
        </mat-autocomplete>
      </div>

      @if (contactNameControl.value && (hasSelectedContact || createNew)) {
        <div class="flex flex-col gt-xs:flex-row">
          <!-- Type/Category Control -->
          @if (hasSelectedContact) {
            <mat-form-field class="flex-auto gt-xs:pr-3">
              <mat-label>Contact Type</mat-label>
              <input
                matInput
                class="cursor-not-allowed"
                [formControlName]="'category_name'"
                [placeholder]="'Contact Type'"
                [readonly]="true"
              />
            </mat-form-field>
          }
          @if (createNew) {
            <mat-form-field class="flex-auto gt-xs:pr-3">
              <mat-label>Contact Type</mat-label>
              <mat-select [formControlName]="'category_id'">
                @for (category of categories; track category.id) {
                  <mat-option [value]="category.id">{{ category.name }}</mat-option>
                }
              </mat-select>
              @if (contactForm.get('category_id').hasError('required') && contactForm.get('category_id').touched) {
                <mat-hint>Contact type is required.</mat-hint>
              }
            </mat-form-field>
          }

          <!-- Associated Company Control -->
          @if (hasSelectedContact) {
            <mat-form-field class="flex-auto cursor-not-allowed">
              <mat-label>Associated Company</mat-label>
              <input
                matInput
                class="cursor-not-allowed"
                [formControlName]="'company_name'"
                [placeholder]="'Associated company'"
                [readonly]="true"
              />
            </mat-form-field>
          }
          @if (createNew) {
            <mat-form-field class="flex-auto">
              <mat-label>Associated Company</mat-label>
              <input
                matInput
                [formControlName]="'company_name'"
                [placeholder]="'Start typing...'"
                [matAutocomplete]="matAutocompleteCompany"
                [spellcheck]="false"
              />
              @if (isAssociatedCompanySearching) {
                <div [ngTemplateOutlet]="LoadingSpinner"></div>
              }
              @if (associatedCompanyResults && !associatedCompanyResults.length) {
                <mat-hint>Must associate contact to an existing company</mat-hint>
              }
            </mat-form-field>
            <mat-autocomplete
              #matAutocompleteCompany="matAutocomplete"
              [disableRipple]="true"
              (optionSelected)="autocompleteAssociatedCompanySelected($event)"
            >
              @if (associatedCompanyResults && !associatedCompanyResults.length) {
                <mat-option disabled class="no-results">No companies found!</mat-option>
              }

              @for (associatedCompanyResult of associatedCompanyResults; track associatedCompanyResult) {
                <mat-option [value]="associatedCompanyResult">
                  {{ associatedCompanyResult }}
                </mat-option>
              }
            </mat-autocomplete>
          }
        </div>

        <div class="flex flex-col gt-xs:flex-row">
          <!-- Email Control -->
          <mat-form-field class="flex-auto gt-xs:pr-3">
            <mat-label>Email</mat-label>
            <input
              matInput
              [ngClass]="{ 'cursor-not-allowed': hasSelectedContact }"
              [formControlName]="'email'"
              [placeholder]="'Email'"
              [readonly]="hasSelectedContact"
            />
            @if (createNew) {
              @if (contactForm.get('email')?.hasError('pattern') && contactForm.get('email')?.touched) {
                <mat-hint>Please enter a valid email address.</mat-hint>
              }
            }
          </mat-form-field>

          <!-- Phone Control -->
          <mat-form-field class="flex-auto">
            <mat-label>Phone</mat-label>
            <input
              matInput
              [formControlName]="'phone'"
              [placeholder]="'Phone'"
              [ngClass]="{ 'cursor-not-allowed': hasSelectedContact }"
              [value]="contactForm.get('phone').value | phone"
              [readonly]="hasSelectedContact"
              autocomplete="off"
              digitOnly
            />
          </mat-form-field>
        </div>

        <!-- Contact Tags Control -->
        <div class="flex flex-col gt-xs:flex-row">
          @if (hasSelectedContact && selectedContact?.tag_list.length) {
            <mat-form-field class="flex-auto cursor-not-allowed">
              <mat-label>Tags</mat-label>
              <mat-chip-list #chipList aria-label="Tags display">
                @for (tag of tags; track tag) {
                  <mat-chip class="cursor-not-allowed" [selectable]="false" [removable]="false">{{ tag }}</mat-chip>
                }
              </mat-chip-list>
            </mat-form-field>
          }
          @if (createNew) {
            <mat-form-field class="flex-auto">
              <mat-label>Tags</mat-label>
              <mat-chip-list #chipList aria-label="Tag selection">
                @for (tag of tags; track tag) {
                  <mat-chip [selectable]="true" [removable]="true" (removed)="removeTag(tag)">
                    {{ tag }}
                    <mat-icon matChipRemove>cancel</mat-icon>
                  </mat-chip>
                }
                <input
                  placeholder="New tag..."
                  #tagsInput
                  [matAutocomplete]="auto"
                  [formControlName]="'tagsInput'"
                  [matChipInputFor]="chipList"
                  [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                  (matChipInputTokenEnd)="addTag($event)"
                  (focus)="tagsInput.focused = true"
                  (blur)="tagsInput.focused = false"
                  data-cy="tags-input"
                />
              </mat-chip-list>
              <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selectedTag($event)">
                @if (tagsSearchResult$ | async; as tagsSearchResult) {
                  @for (tag of tagsSearchResult | keyvalue; track tag.key) {
                    <mat-option [value]="tag.key">{{ tag.key }}</mat-option>
                  }
                }
              </mat-autocomplete>
              @if (tagsInput?.focused) {
                <mat-hint class="mt-2">
                  Press <strong>TAB</strong> key after finishing typing to add a new tag
                </mat-hint>
              }
            </mat-form-field>
          }
        </div>
      }
    </form>
  </main>
  <footer ts-dialog-actions class="ts-dialog-actions-container">
    @if (hasSelectedContact) {
      <ts-button
        variant="secondary"
        [matTooltip]="'Clear all fields and search for a new contact'"
        (click)="resetForm()"
      >
        Clear
      </ts-button>
    }
    @if (error) {
      <mat-error class="w-1/2 px-4 mx-auto my-0">{{ error?.error.message }}</mat-error>
    }
    <ts-button variant="secondary" (click)="discard()">Cancel</ts-button>
    <ts-button variant="primary" [disabled]="isSaveBtnDisabled" (click)="saveContactAssociation()">
      @if (!isLoading) {
        {{ createNew ? 'Create and Save' : 'Save' }}
      } @else {
        <div [ngTemplateOutlet]="LoadingSpinner"></div>
      }
    </ts-button>
  </footer>
</ts-dialog>

<ng-template #LoadingSpinner>
  <mat-progress-spinner [diameter]="24" [mode]="'indeterminate'"></mat-progress-spinner>
</ng-template>
